import { Building2, Loader2, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { economicGroupApi, type IEconomicGroup } from '@/api/commercial/economic-groups';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebounceFn } from '@/hooks/use-debounce';
import { EconomicGroupFormDialog } from '../economic-group-form-dialog';

const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

export interface EconomicGroupUpdateResult {
  selectedGroupId: string | null;
  holdingName?: string;
  holdingTaxId?: string;
}

interface EconomicGroupUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EconomicGroupUpdateResult) => void;
  currentGroupId?: string;
  accountTaxId: string;
  accountName: string;
}

export function EconomicGroupUpdateDialog({
  open,
  onClose,
  onSubmit,
  currentGroupId,
  accountTaxId,
  accountName,
}: EconomicGroupUpdateDialogProps) {
  const [holdingSearch, setHoldingSearch] = useState('');
  const [searchResults, setSearchResults] = useState<IEconomicGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IEconomicGroup | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Load current group data when dialog opens
  useEffect(() => {
    if (open) {
      setHoldingSearch('');
      setSearchResults([]);
      setShowDropdown(false);

      if (currentGroupId) {
        setIsLoadingCurrent(true);
        economicGroupApi
          .findMany({ search: '', limit: 100 })
          .then((result) => {
            const match = result.docs.find((g) => g.id === currentGroupId);
            setSelectedGroup(match || null);
          })
          .catch(() => setSelectedGroup(null))
          .finally(() => setIsLoadingCurrent(false));
      } else {
        setSelectedGroup(null);
      }
    }
  }, [open, currentGroupId]);

  const handleSearch = useCallback(async (value: string) => {
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const result = await economicGroupApi.findMany({ search: value, limit: 10 });
      setSearchResults(result.docs || []);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const debouncedSearch = useDebounceFn(handleSearch, 500);

  const handleSelectGroup = (group: IEconomicGroup) => {
    setSelectedGroup(group);
    setHoldingSearch('');
    setShowDropdown(false);
  };

  const handleRemoveGroup = () => {
    setSelectedGroup(null);
  };

  const handleCreateGroup = async (data: Omit<IEconomicGroup, 'id'>) => {
    setIsCreating(true);
    try {
      const result = await economicGroupApi.create(data);
      if (result.success && result.data?.id) {
        const groups = await economicGroupApi.findMany({ search: '', limit: 100 });
        const newGroup = groups.docs.find((g) => g.id === result.data?.id);
        if (newGroup) {
          setSelectedGroup(newGroup);
        }
      }
    } finally {
      setIsCreating(false);
      setIsCreateFormOpen(false);
    }
  };

  const handleSubmit = () => {
    onSubmit({
      selectedGroupId: selectedGroup?.id || null,
      holdingName: selectedGroup?.economic_group_holding?.name,
      holdingTaxId: selectedGroup?.economic_group_holding?.taxId,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vincular a Grupo Econômico</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Holding Section */}
          <div className="space-y-3">
            <Label>Holding (Empresa Controladora)</Label>

            {isLoadingCurrent ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : selectedGroup ? (
              <div className="space-y-3">
                {selectedGroup.economic_group_holding && (
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                    <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {selectedGroup.economic_group_holding.name}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {formatCnpj(selectedGroup.economic_group_holding.taxId)}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      Holding
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={handleRemoveGroup}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Show controladas preview (including current account inline) */}
                {(() => {
                  const controlled = selectedGroup.economic_group_controlled ?? [];
                  const currentAccountIsListed = controlled.some((c) => c.taxId === accountTaxId);
                  const totalCount = currentAccountIsListed
                    ? controlled.length
                    : controlled.length + 1;

                  return totalCount > 0 ? (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase">
                        Controladas ({totalCount})
                      </p>
                      {controlled.map((c) => {
                        const isCurrentAccount = c.taxId === accountTaxId;
                        return (
                          <div
                            key={c.taxId}
                            className={
                              isCurrentAccount
                                ? 'flex items-center gap-2 p-3 border rounded-lg bg-blue-50 border-blue-200'
                                : 'flex items-center gap-2 p-3 border rounded-lg bg-muted/30'
                            }
                          >
                            <Building2
                              className={
                                isCurrentAccount
                                  ? 'h-4 w-4 text-blue-600 flex-shrink-0'
                                  : 'h-4 w-4 text-muted-foreground flex-shrink-0'
                              }
                            />
                            <div className="flex-1 min-w-0">
                              <p
                                className={
                                  isCurrentAccount
                                    ? 'font-medium text-sm truncate text-blue-800'
                                    : 'font-medium text-sm truncate'
                                }
                              >
                                {c.name}
                              </p>
                              <p
                                className={
                                  isCurrentAccount
                                    ? 'text-xs font-mono text-blue-600'
                                    : 'text-xs text-muted-foreground font-mono'
                                }
                              >
                                {formatCnpj(c.taxId)}
                              </p>
                            </div>
                            {isCurrentAccount ? (
                              <Badge
                                className="text-xs bg-blue-100 text-blue-700 border-blue-300 flex-shrink-0"
                                variant="outline"
                              >
                                Esta conta
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                Controlada
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                      {!currentAccountIsListed && (
                        <div className="flex items-center gap-2 p-3 border rounded-lg bg-blue-50 border-blue-200">
                          <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate text-blue-800">
                              {accountName}
                            </p>
                            <p className="text-xs font-mono text-blue-600">
                              {formatCnpj(accountTaxId)}
                            </p>
                          </div>
                          <Badge
                            className="text-xs bg-blue-100 text-blue-700 border-blue-300 flex-shrink-0"
                            variant="outline"
                          >
                            Esta conta
                          </Badge>
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar grupo econômico por nome ou CNPJ..."
                    value={holdingSearch}
                    onChange={(e) => {
                      setHoldingSearch(e.target.value);
                      setShowDropdown(true);
                      debouncedSearch(e.target.value);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="pl-9"
                  />
                </div>

                {isSearching && (
                  <div className="absolute z-50 w-full mt-1 border rounded-lg bg-background shadow-lg p-3 text-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground inline" />
                    <span className="ml-2 text-sm text-muted-foreground">Buscando...</span>
                  </div>
                )}

                {showDropdown && !isSearching && searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 border rounded-lg bg-background shadow-lg">
                    <ScrollArea className="max-h-48">
                      {searchResults.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                          onClick={() => handleSelectGroup(group)}
                        >
                          <p className="font-medium text-sm">
                            {group.economic_group_holding?.name || 'Grupo sem holding'}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {group.economic_group_holding?.taxId
                              ? formatCnpj(group.economic_group_holding.taxId)
                              : '-'}
                          </p>
                          {group.economic_group_controlled &&
                            group.economic_group_controlled.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {group.economic_group_controlled.length} controlada(s)
                              </p>
                            )}
                        </button>
                      ))}
                    </ScrollArea>
                  </div>
                )}

                {showDropdown && !isSearching && holdingSearch && searchResults.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 border rounded-lg bg-background shadow-lg p-3 text-center">
                    <p className="text-sm text-muted-foreground">Nenhum grupo encontrado</p>
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => setIsCreateFormOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Criar novo grupo
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit}>Salvar</Button>
        </DialogFooter>
      </DialogContent>

      <EconomicGroupFormDialog
        open={isCreateFormOpen}
        onClose={() => setIsCreateFormOpen(false)}
        mode="create"
        onSubmit={handleCreateGroup}
        isPending={isCreating}
        accountTaxId={accountTaxId}
        accountName={accountName}
      />
    </Dialog>
  );
}
